import { useMemo, useState } from "react";
import { Overlay } from "../../../../styles/popup";
import { TournamentTicketResponseForSeller } from "../../../../api/tournament-ticket";
import * as S from "./styles";

interface BuyPopupProps {
  ticket: TournamentTicketResponseForSeller;
  pubCarrot: number;
  onClose: () => void;
  onConfirm: (count: number) => Promise<boolean>;
}

const BuyPopup = ({ ticket, pubCarrot, onClose, onConfirm }: BuyPopupProps) => {
  const [countInput, setCountInput] = useState("1");
  const [loading, setLoading] = useState(false);

  const unitPrice = ticket.sellerPrice ?? 0;
  const count = Number(countInput);
  const validCount = Number.isInteger(count) && count > 0 ? count : 0;
  const buyCount = ticket.buyCount ?? 0;
  const buyLimitCount = ticket.buyLimitCount ?? null;

  const totalDeduct = unitPrice * validCount;
  const afterCarrot = pubCarrot - totalDeduct;
  const afterTicketCount = ticket.hasCount + validCount;
  const remainingBuyCount =
    buyLimitCount == null ? null : Math.max(buyLimitCount - buyCount, 0);

  const isDisabled = useMemo(() => {
    if (!countInput || validCount <= 0) return true;
    if (ticket.remainCount !== null && validCount > ticket.remainCount) {
      return true;
    }
    if (remainingBuyCount !== null && validCount > remainingBuyCount) {
      return true;
    }
    if (totalDeduct > pubCarrot) return true;
    return loading;
  }, [
    countInput,
    validCount,
    ticket.remainCount,
    remainingBuyCount,
    totalDeduct,
    pubCarrot,
    loading,
  ]);

  const handleConfirm = async () => {
    if (isDisabled) return;

    const confirmed = window.confirm(
      `${ticket.name} 티켓 ${validCount}장을\n${totalDeduct.toLocaleString()} 캐럿으로 교환하시겠습니까?`,
    );
    if (!confirmed) return;

    setLoading(true);
    try {
      const success = await onConfirm(validCount);
      if (success) onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay $zIndex={1000}>
      <S.Popup>
        <S.Header>
          <div className="title">티켓 교환</div>
          <img
            src="/image-web/tournament/close.svg"
            alt="close"
            onClick={onClose}
          />
        </S.Header>

        <S.InfoBox>
          <S.InfoRow>
            <div className="label">티켓명</div>
            <div className="value">{ticket.name}</div>
          </S.InfoRow>
          <S.InfoRow>
            <div className="label">보유 캐럿</div>
            <div className="value">{pubCarrot.toLocaleString()} CR</div>
          </S.InfoRow>
          <S.InfoRow>
            <div className="label">캐럿 가치</div>
            <div className="value">{unitPrice.toLocaleString()} CR</div>
          </S.InfoRow>
          <S.InfoRow>
            <div className="label">현재 티켓 보유 수량</div>
            <div className="value">{ticket.hasCount.toLocaleString()}장</div>
          </S.InfoRow>
          <S.InfoRow>
            <div className="label">누적 교환 / 상한</div>
            <div className="value">
              {buyCount.toLocaleString()}장{" / "}
              {buyLimitCount == null
                ? "무제한"
                : `${buyLimitCount.toLocaleString()}장`}
            </div>
          </S.InfoRow>
          {remainingBuyCount !== null && (
            <S.InfoRow>
              <div className="label">추가 교환 가능</div>
              <div className="value">
                {remainingBuyCount.toLocaleString()}장
              </div>
            </S.InfoRow>
          )}
        </S.InfoBox>

        <S.QuantitySection>
          <div className="label">교환 수량</div>
          <input
            type="number"
            min={1}
            max={remainingBuyCount ?? undefined}
            value={countInput}
            onChange={(e) => setCountInput(e.target.value)}
          />
        </S.QuantitySection>

        <S.Divider />

        <S.SummaryRow $highlight>
          <div className="label">총 차감 캐럿</div>
          <div className="value">{totalDeduct.toLocaleString()} CR</div>
        </S.SummaryRow>
        <S.SummaryRow>
          <div className="label">교환 후 보유 캐럿</div>
          <div className="value">{afterCarrot.toLocaleString()} CR</div>
        </S.SummaryRow>
        <S.SummaryRow>
          <div className="label">교환 후 티켓 보유 수량</div>
          <div className="value">{afterTicketCount.toLocaleString()} 장</div>
        </S.SummaryRow>

        <S.ButtonWrapper>
          <div className="cancel" onClick={onClose}>
            취소
          </div>
          <div
            className={`confirm ${isDisabled ? "disabled" : ""}`}
            onClick={handleConfirm}
          >
            확인
          </div>
        </S.ButtonWrapper>
      </S.Popup>
    </Overlay>
  );
};

export default BuyPopup;
